import { Button, ErrorState, LoadingState, Pagination, Text } from '@cinesend/ui-kit'
import { ArrowDown, ArrowUp, Plus, Trash } from '@phosphor-icons/react'
import { Link } from 'react-router-dom'
import { TABLE_PAGE_SIZES } from '../../constants/table_page_sizes'
import { convertToLongDate } from '../../helpers/convert_date'

const formatDate = (value) => (value ? convertToLongDate(value) : null)

const SectionItems = ({
  title = 'Included categories',
  description = 'Categories and the order they appear in this section.',
  emptyText = 'No categories in this section yet.',
  addLabel = 'Add category',
  removeLabel = (name) => `Remove ${name} from section`,
  subtitle,
  items,
  isLoading,
  isFetching,
  error,
  onRetry,
  getItemName,
  itemPath,
  canReorder,
  onMove,
  onRemove,
  onAdd,
  pagination = null,
  totalRows = 0,
}) => {
  const page = pagination ? pagination.currentPage + 1 : 1
  const from = pagination && items.length ? (page - 1) * pagination.rowsPerPage + 1 : null

  return (
    <section className='flex w-full flex-col gap-4'>
      <div className='flex flex-col gap-1'>
        <Text as='h2' variant='md-medium'>
          {title}
        </Text>
        <Text as='p' variant='sm' tone='secondary'>
          {description}
        </Text>
      </div>
      <div className='flex flex-col gap-4 rounded-2xl border border-[color:var(--cs-color-border-default)] bg-[var(--cs-color-background-primary)] p-6'>
        {error ? (
          <ErrorState
            message={error.message ?? 'An error occurred. Please contact customer support.'}
            onRetry={onRetry}
          />
        ) : isLoading ? (
          <LoadingState className='justify-center' />
        ) : items.length === 0 ? (
          <Text as='p' variant='sm' tone='secondary'>
            {emptyText}
          </Text>
        ) : (
          <ul className='m-0 flex list-none flex-col gap-2 p-0'>
            {items.map((item, index) => {
              const name = getItemName(item)
              const dates = [formatDate(item.start_date), formatDate(item.end_date)].filter(Boolean).join(' – ')
              const detail = subtitle ? subtitle(item, dates) : dates
              const path = itemPath?.(item)
              const nameBlock = (
                <>
                  <Text as='span' variant='sm-medium' className='block truncate'>
                    {name}
                  </Text>
                  {detail ? (
                    <Text as='span' variant='xs' tone='secondary' className='mt-0.5 block truncate'>
                      {detail}
                    </Text>
                  ) : null}
                </>
              )
              return (
                <li
                  key={item.id}
                  className='flex items-center gap-3 rounded-lg border border-[color:var(--cs-color-border-subtle)] px-3 py-2'
                >
                  {path ? (
                    <Link to={path} className='min-w-0 flex-1'>
                      {nameBlock}
                    </Link>
                  ) : (
                    <div className='min-w-0 flex-1'>{nameBlock}</div>
                  )}
                  <div className='flex shrink-0 items-center'>
                    {canReorder ? (
                      <>
                        <Button
                          variant='ghost'
                          iconOnly
                          tooltip='Move up'
                          disabled={index === 0}
                          leadingIcon={<ArrowUp size={20} weight='regular' aria-hidden='true' />}
                          onClick={() => onMove(index, -1)}
                        >
                          Move up
                        </Button>
                        <Button
                          variant='ghost'
                          iconOnly
                          tooltip='Move down'
                          disabled={index === items.length - 1}
                          leadingIcon={<ArrowDown size={20} weight='regular' aria-hidden='true' />}
                          onClick={() => onMove(index, 1)}
                        >
                          Move down
                        </Button>
                      </>
                    ) : null}
                    <Button
                      variant='ghost'
                      iconOnly
                      tooltip='Remove'
                      leadingIcon={<Trash size={20} weight='regular' aria-hidden='true' />}
                      onClick={() => onRemove(item)}
                    >
                      {removeLabel(name)}
                    </Button>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
        <div className='flex items-center gap-4 py-2'>
          <div className='h-px min-w-0 flex-1 bg-[var(--cs-color-border-subtle)]' aria-hidden='true' />
          <Button variant='ghost' leadingIcon={<Plus size={20} weight='regular' aria-hidden='true' />} onClick={onAdd}>
            {addLabel}
          </Button>
          <div className='h-px min-w-0 flex-1 bg-[var(--cs-color-border-subtle)]' aria-hidden='true' />
        </div>
      </div>
      {pagination && totalRows > 0 ? (
        <Pagination
          compact
          page={page}
          pageCount={Math.ceil(totalRows / pagination.rowsPerPage) || 1}
          from={from}
          to={from ? from + items.length - 1 : null}
          total={totalRows}
          pageSize={pagination.onRowsPerPageChange ? pagination.rowsPerPage : undefined}
          pageSizeOptions={TABLE_PAGE_SIZES}
          onPageSizeChange={pagination.onRowsPerPageChange}
          onPage={(next) => pagination.onPageChange(next - 1)}
          isFetching={isFetching}
        />
      ) : null}
    </section>
  )
}

export default SectionItems
